import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiFetch, isEmailAuthMode } from '@/lib/api-client';
import i18n from '@/i18n';

type Theme = 'light' | 'dark' | 'system';
export type DesignVariant = 'default' | 'figma';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  designVariant: DesignVariant;
  setDesignVariant: (variant: DesignVariant) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('light'); // Default to light
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
  const [designVariant, setDesignVariantState] = useState<DesignVariant>('default');
  const [isInitialized, setIsInitialized] = useState(false);

  // Get system preference
  const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  };

  // Resolve theme (system -> light/dark)
  const resolveTheme = (theme: Theme): 'light' | 'dark' => {
    if (theme === 'system') {
      return getSystemTheme();
    }
    return theme;
  };

  // Apply theme to document
  const applyTheme = (theme: 'light' | 'dark') => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    setResolvedTheme(theme);
  };

  // Apply design variant to document (data-theme-variant="figma" or remove)
  const applyDesignVariant = (variant: DesignVariant) => {
    const root = document.documentElement;
    if (variant === 'figma') {
      root.setAttribute('data-theme-variant', 'figma');
    } else {
      root.removeAttribute('data-theme-variant');
    }
  };

  // Load theme from user preferences or localStorage
  useEffect(() => {
    if (isInitialized) return;

    const loadTheme = async () => {
      let themeLoaded = false;
      let variantLoaded = false;

      if (isEmailAuthMode()) {
        try {
          const res = await apiFetch('/api/user/preferences', {
            credentials: 'include',
            cache: 'no-store',
          });
          if (res.ok) {
            const data = await res.json();
            const prefs = data.preferences || {};
            const userTheme = prefs.theme as Theme | undefined;
            if (userTheme && ['light', 'dark', 'system'].includes(userTheme)) {
              setThemeState(userTheme);
              applyTheme(resolveTheme(userTheme));
              themeLoaded = true;
            }
            const userDesignVariant = prefs.designVariant as DesignVariant | undefined;
            if (userDesignVariant && ['default', 'figma'].includes(userDesignVariant)) {
              setDesignVariantState(userDesignVariant);
              applyDesignVariant(userDesignVariant);
              variantLoaded = true;
            }
            // Sync app locale from user preferences (same on all devices)
            const userLocale = prefs.locale as string | undefined;
            if (userLocale && ['hu', 'sk', 'en'].includes(userLocale)) {
              i18n.changeLanguage(userLocale);
            } else {
              const cur = i18n.language.split('-')[0];
              if (cur === 'hu' || cur === 'sk' || cur === 'en') {
                void apiFetch('/api/user/preferences', {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ preferences: { locale: cur } }),
                }).catch(() => {});
              }
            }
          }
        } catch (error) {
          console.error('[Theme] Error loading user preferences:', error);
        }
      }

      if (!themeLoaded) {
        const stored = localStorage.getItem('theme') as Theme | null;
        const initialTheme: Theme = stored || 'light';
        setThemeState(initialTheme);
        applyTheme(resolveTheme(initialTheme));
      }
      if (!variantLoaded) {
        const storedVariant = localStorage.getItem('designVariant') as DesignVariant | null;
        const initialVariant: DesignVariant = storedVariant && ['default', 'figma'].includes(storedVariant) ? storedVariant : 'default';
        setDesignVariantState(initialVariant);
        applyDesignVariant(initialVariant);
      }
      setIsInitialized(true);
    };

    loadTheme();
  }, [isInitialized]);

  // Listen for system theme changes (only if theme is 'system')
  useEffect(() => {
    if (theme !== 'system' || !isInitialized) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      applyTheme(e.matches ? 'dark' : 'light');
    };

    // Initial check
    handleChange(mediaQuery);

    // Listen for changes (modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme, isInitialized]);

  // Handle theme changes
  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    const resolved = resolveTheme(newTheme);
    applyTheme(resolved);

    try {
      if (isEmailAuthMode()) {
        await apiFetch('/api/user/preferences', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ preferences: { theme: newTheme } }),
        });
      } else {
        localStorage.setItem('theme', newTheme);
      }
    } catch (error) {
      console.error('[Theme] Error saving user preferences:', error);
      localStorage.setItem('theme', newTheme);
    }
  };

  // Handle design variant changes
  const setDesignVariant = async (newVariant: DesignVariant) => {
    setDesignVariantState(newVariant);
    applyDesignVariant(newVariant);

    try {
      if (isEmailAuthMode()) {
        await apiFetch('/api/user/preferences', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ preferences: { designVariant: newVariant } }),
        });
      } else {
        localStorage.setItem('designVariant', newVariant);
      }
    } catch (error) {
      console.error('[Theme] Error saving design variant:', error);
      localStorage.setItem('designVariant', newVariant);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, designVariant, setDesignVariant }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}